/**
 * Contrato de columnas y plantilla de Excel del importador de Ventas de Libros.
 *
 * Sin React ni antd a propósito: el workbook se puede generar y revisar con un
 * `node script.js`, sin levantar la app.
 *
 * El archivo real lo genera el formulario de Google, pero la primera hoja de la
 * plantilla ("Respuestas") es el formato exacto que se espera — mismos
 * encabezados, mismo orden — para poder capturar a mano cuando no hay
 * formulario. Va primero **porque el importador siempre lee la primera hoja del
 * archivo que se sube**: si las instrucciones estuvieran antes, el archivo
 * llenado a mano se subiría vacío.
 */

import * as XLSXStyle from "xlsx-js-style";
import { border, headerStyle } from "../../../../../Components/ExcelEstilos";

const COLOR_TITULO = "007A8A";
const COLOR_SUAVE = "D6F4F8";

/**
 * Fuente única de la verdad de las columnas: de aquí salen tanto la detección de
 * encabezados de la pantalla como las hojas de la plantilla.
 *
 * `prefijo` va normalizado (sin acentos, en minúsculas, sin signos): el
 * formulario de Google trae encabezados larguísimos, con saltos de línea y emoji
 * ("CORREO ELECTRÓNICO\n⚠️ En este correo estarás recibiendo tus códigos..."), y
 * cambian cada semestre, así que se reconocen por su prefijo y no por el texto
 * exacto. El orden de este arreglo no afecta la detección (cada columna se busca
 * por su cuenta); es el orden en que se pintan las hojas.
 */
export const COLUMNAS_ESPERADAS = [
  {
    clave: "matricula",
    prefijo: "numero de control",
    etiqueta: "NÚMERO DE CONTROL",
    ancho: 22,
    ejemplo: "EJEMPLO-001",
    ayuda:
      "Matrícula del alumno. Es lo único con lo que se le identifica: si no coincide con ninguna ficha de alumno, la fila no se importa.",
  },
  {
    clave: "nombre",
    prefijo: "nombre",
    etiqueta: "NOMBRE(S)",
    ancho: 20,
    ejemplo: "Ana Sofía",
    ayuda:
      "Informativo. El nombre que se usa en la venta es el de la ficha del alumno, no el que venga en el archivo.",
  },
  {
    clave: "apellidoPaterno",
    prefijo: "apellido paterno",
    etiqueta: "APELLIDO PATERNO",
    ancho: 20,
    ejemplo: "Pérez",
    ayuda: "Informativo, igual que el nombre.",
  },
  {
    clave: "apellidoMaterno",
    prefijo: "apellido materno",
    etiqueta: "APELLIDO MATERNO",
    ancho: 20,
    ejemplo: "López",
    ayuda: "Informativo, igual que el nombre.",
  },
  {
    clave: "correo",
    prefijo: "correo electronico",
    etiqueta: "CORREO ELECTRÓNICO",
    ancho: 32,
    ejemplo: "ana.perez@correo.com",
    ayuda:
      "A dónde se manda el código. También se manda a los correos que el alumno ya tenga en su ficha y, si éste es nuevo, se le guarda.",
  },
  {
    clave: "material",
    prefijo: "material a solicitar",
    etiqueta: "MATERIAL A SOLICITAR",
    ancho: 34,
    ejemplo: "Four Corners 3 - Nivel 5 y 6",
    ayuda:
      'Informativo. El libro sale de la Lista Escolar del alumno; esto sólo sirve para marcar "No coincide" cuando pidió algo que no está en su lista.',
  },
  {
    clave: "revision",
    prefijo: "columna 1",
    etiqueta: "Columna 1",
    ancho: 18,
    ejemplo: "CONFIRMADO",
    ayuda:
      'La columna de revisión del formulario, donde la escuela marca que el pago ya se validó. Sólo se importan las filas que dicen CONFIRMADO; las demás se ven en la vista previa como "Omitir".',
  },
];

// El material NO es obligatorio: el libro sale de la Lista Escolar del alumno.
export const COLUMNAS_OBLIGATORIAS = ["matricula", "revision"];

export const NOMBRE_PLANTILLA = "plantillaVentasLibros.xlsx";

// ─── Estilos ─────────────────────────────────────────────────────────────────

const tituloStyle = {
  font: { bold: true, sz: 13, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: COLOR_TITULO } },
  alignment: { horizontal: "left", vertical: "center" },
};

const subtituloStyle = {
  font: { bold: true, sz: 11, color: { rgb: "0B4F5A" } },
  fill: { fgColor: { rgb: COLOR_SUAVE } },
  alignment: { vertical: "center", wrapText: true },
};

const notaStyle = {
  font: { sz: 10, color: { rgb: "434343" } },
  alignment: { vertical: "center", wrapText: true },
};

const avisoStyle = {
  font: { bold: true, sz: 10, color: { rgb: "A8071A" } },
  alignment: { vertical: "center", wrapText: true },
};

const celdaStyle = { border, alignment: { vertical: "center", wrapText: true } };

const celdaCentradaStyle = {
  border,
  alignment: { vertical: "center", horizontal: "center" },
};

const numeroStyle = {
  border,
  alignment: { vertical: "center", horizontal: "right" },
};

// La fila de ejemplo va en gris cursivo para que se lea como lo que es: algo que
// hay que borrar antes de subir el archivo.
const ejemploStyle = {
  font: { italic: true, sz: 10, color: { rgb: "8C8C8C" } },
  border,
  alignment: { vertical: "center", wrapText: true },
};

const faltaStyle = {
  font: { bold: true, sz: 10, color: { rgb: "A8071A" } },
  border,
  alignment: { vertical: "center", horizontal: "center" },
};

const okStyle = {
  font: { sz: 10, color: { rgb: "237804" } },
  border,
  alignment: { vertical: "center", horizontal: "center" },
};

// ─── Celdas ──────────────────────────────────────────────────────────────────

const celda = (valor, estilo = celdaStyle) => ({
  v: valor === undefined || valor === null ? "" : String(valor),
  t: "s",
  s: estilo,
});

// xlsx-js-style NO infiere el tipo cuando el elemento del arreglo ya es un objeto
// {v,s}: sin `t: "n"` explícito el número se escribe como texto y Excel lo marca
// como "número almacenado como texto".
const celdaNumero = (valor, estilo = numeroStyle) =>
  valor || valor === 0
    ? { v: Number(valor), t: "n", s: estilo }
    : celda("", estilo);

const hojaConEncabezado = (encabezados, filas, anchos) => {
  const hoja = XLSXStyle.utils.aoa_to_sheet([
    encabezados.map((titulo) => celda(titulo, headerStyle)),
    ...filas,
  ]);
  hoja["!cols"] = anchos.map((wch) => ({ wch }));
  hoja["!rows"] = [{ hpt: 26 }];
  return hoja;
};

// ─── Hojas ───────────────────────────────────────────────────────────────────

/**
 * Hoja 1: el formato que se sube. Encabezados en la primera fila (el importador
 * los lee de ahí) y una sola fila de ejemplo.
 */
const hojaRespuestas = () =>
  hojaConEncabezado(
    COLUMNAS_ESPERADAS.map((columna) => columna.etiqueta),
    [COLUMNAS_ESPERADAS.map((columna) => celda(columna.ejemplo, ejemploStyle))],
    COLUMNAS_ESPERADAS.map((columna) => columna.ancho),
  );

const LINEAS_INSTRUCCIONES = [
  [tituloStyle, "IMPORTAR VENTAS DE LIBROS"],
  [notaStyle, "Panel → Ventas eCommerce → botón Importar Ventas de Libros."],
  [notaStyle, ""],
  [subtituloStyle, "Qué hace con cada alumno confirmado"],
  [
    notaStyle,
    "1. Crea la venta: una remisión de contado con un renglón por cada libro marcado como Libro Virtual en su Lista Escolar.",
  ],
  [
    notaStyle,
    "2. Registra el cobro en el banco que elegiste y genera la póliza de ingreso.",
  ],
  [
    notaStyle,
    "3. Le entrega el código del libro, descontándolo del inventario.",
  ],
  [
    notaStyle,
    "4. Le manda por correo el código, al del archivo y a los que ya tenga en su ficha.",
  ],
  [notaStyle, ""],
  [subtituloStyle, "Cómo se usa este archivo"],
  [
    notaStyle,
    'Lo normal es subir tal cual el Excel de respuestas del formulario de Google: no hay que reacomodar nada, las columnas se reconocen por su encabezado (ver la hoja "Columnas").',
  ],
  [
    notaStyle,
    'Si no hay formulario, captura a mano en la hoja "Respuestas" de este archivo, respetando los encabezados de la primera fila, y súbelo.',
  ],
  [
    avisoStyle,
    "Borra la fila de ejemplo antes de subir el archivo: se reporta como error porque esa matrícula no existe.",
  ],
  [
    notaStyle,
    "El importador siempre lee la PRIMERA hoja del archivo. No cambies el orden de las hojas ni agregues otra antes de Respuestas.",
  ],
  [notaStyle, ""],
  [subtituloStyle, "Antes de importar"],
  [
    notaStyle,
    "Elige Proyecto, Banco y Forma de Pago arriba de la tabla. El banco tiene que ser en pesos.",
  ],
  [
    notaStyle,
    'Revisa la vista previa: cada fila dice si se va a crear la venta, si se omite y por qué. Las que dicen "Revisar" son problemas de catálogo (lista sin libro, libro sin precio, libro sin código del SAT) y hay que arreglarlos antes.',
  ],
  [
    notaStyle,
    'La hoja "Libros Virtuales" de este archivo te dice cuántos códigos hay libres de cada libro. Si no alcanzan, la venta se crea igual pero se queda sin código y sin correo.',
  ],
  [notaStyle, ""],
  [subtituloStyle, "Qué NO hace"],
  [
    notaStyle,
    "No elige el libro: siempre sale de la Lista Escolar del alumno (la suya o la de su grupo). Lo que el alumno escribió en el formulario sólo se usa para avisarte que no coincide.",
  ],
  [
    notaStyle,
    "No factura: la venta se crea como remisión. Si un alumno pide factura, se factura después con el flujo normal de Facturar Remisión.",
  ],
  [
    notaStyle,
    "No duplica: si el alumno ya tiene una venta no cancelada, se omite y se te indica el folio que ya tiene.",
  ],
];

const hojaInstrucciones = () => {
  const hoja = XLSXStyle.utils.aoa_to_sheet(
    LINEAS_INSTRUCCIONES.map(([estilo, texto]) => [celda(texto, estilo)]),
  );
  hoja["!cols"] = [{ wch: 118 }];
  hoja["!rows"] = LINEAS_INSTRUCCIONES.map(([estilo]) =>
    estilo === tituloStyle ? { hpt: 26 } : { hpt: 16 },
  );
  return hoja;
};

const hojaColumnas = () =>
  hojaConEncabezado(
    [
      "Columna",
      "Obligatoria",
      "Se reconoce si el encabezado empieza con",
      "Para qué sirve",
    ],
    COLUMNAS_ESPERADAS.map((columna) => [
      celda(columna.etiqueta),
      celda(
        COLUMNAS_OBLIGATORIAS.includes(columna.clave) ? "Sí" : "No",
        COLUMNAS_OBLIGATORIAS.includes(columna.clave) ? faltaStyle : celdaCentradaStyle,
      ),
      celda(columna.prefijo),
      celda(columna.ayuda),
    ]),
    [26, 13, 40, 78],
  );

/**
 * Catálogo de consulta: qué libros están marcados como Libro Virtual, si están
 * listos para venderse y cuántos códigos hay libres de cada uno.
 */
const hojaLibros = (catalogos) => {
  const insumos = catalogos?.insumosLibro || [];

  const disponiblePorInsumo = new Map();
  (catalogos?.disponiblesPorInsumo || []).forEach((registro) => {
    const llave = String(registro.insumoId);
    disponiblePorInsumo.set(
      llave,
      (disponiblePorInsumo.get(llave) || 0) + (registro.disponible || 0),
    );
  });

  const filas =
    insumos.length > 0
      ? insumos.map((insumo) => {
          const disponibles = disponiblePorInsumo.get(String(insumo._id)) || 0;
          return [
            celda(insumo.clave),
            celda(insumo.descripcion),
            insumo.codigoSatId
              ? celda("Sí", okStyle)
              : celda("FALTA", faltaStyle),
            insumo.venta ? celda("Sí", okStyle) : celda("No", faltaStyle),
            celdaNumero(disponibles, disponibles > 0 ? numeroStyle : faltaStyle),
          ];
        })
      : [
          [
            celda("(no hay insumos marcados como Libro Virtual)"),
            celda(""),
            celda(""),
            celda(""),
            celda(""),
          ],
        ];

  return hojaConEncabezado(
    [
      "Clave",
      "Descripción",
      "¿Tiene código del SAT?",
      "¿Es de venta?",
      "Códigos libres (todos los proyectos)",
    ],
    filas,
    [18, 46, 18, 14, 24],
  );
};

// ─── Workbook ────────────────────────────────────────────────────────────────

export const construirPlantilla = (catalogos) => {
  const libro = XLSXStyle.utils.book_new();

  XLSXStyle.utils.book_append_sheet(libro, hojaRespuestas(), "Respuestas");
  XLSXStyle.utils.book_append_sheet(libro, hojaInstrucciones(), "Cómo funciona");
  XLSXStyle.utils.book_append_sheet(libro, hojaColumnas(), "Columnas");
  XLSXStyle.utils.book_append_sheet(libro, hojaLibros(catalogos), "Libros Virtuales");

  return libro;
};
